import { useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { Modal } from 'Components/Modal/Modal'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalBody } from 'Components/Modal/ModalBody'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { Checkbox } from 'Components/Inputs/Checkbox/Checkbox'
import { HoverIconCard } from 'Components/HoverIconCard'
import { ModalType, usePeoplePageContext } from 'Contexts/PeoplePageContext'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import {
  useBulkUserManagementQuery,
  useDownloadUserManagementTemplate
} from 'Hooks/api/employer/userManagement/useBulkUserManagementQuery'
import { BulkUserManagementResponse } from 'Services/apiResponseTypes'
import { ButtonContainer, FlexRow, spacingSM, spacingXS } from 'Utils/styles/spacing'
import { AppIconMap, AppIcons } from 'Utils/types/appIcons'
import { UploadView } from './UploadView'
import { DiffPreviewView } from './DiffPreviewView'

type ApiErrorWithTitle = { title: string }
type ApiErrorObject = Record<string, string[]>
type ApiError = string | string[] | ApiErrorWithTitle | ApiErrorWithTitle[] | ApiErrorObject[]

const isApiErrorWithTitle = (error: unknown): error is ApiErrorWithTitle =>
  typeof error === 'object' && error !== null && 'title' in error && typeof error.title === 'string'

const isApiErrorObject = (error: unknown): error is ApiErrorObject =>
  typeof error === 'object' && error !== null && !('title' in error)

const formatErrorsToArray = (errors: ApiError | null | undefined): string[] => {
  if (!errors) return ['Unknown error']

  if (typeof errors === 'string') return [errors]

  if (Array.isArray(errors)) {
    if (errors.length === 0) return ['Unknown error']

    const firstError = errors[0]

    if (typeof firstError === 'string') {
      return errors as string[]
    }

    if (isApiErrorWithTitle(firstError)) {
      return errors.map((e) => (isApiErrorWithTitle(e) ? e.title : String(e)))
    }

    if (isApiErrorObject(firstError)) {
      return errors.flatMap((errObj) => {
        if (isApiErrorObject(errObj)) {
          return Object.values(errObj).flat()
        }
        return [String(errObj)]
      })
    }
  }

  if (isApiErrorWithTitle(errors)) {
    return [errors.title]
  }

  return ['Unknown error format']
}

const formatErrorMessage = (errors: ApiError | null | undefined): string => {
  return formatErrorsToArray(errors).join('; ')
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ErrorContainer = styled.div`
  background-color: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  max-height: 300px;
  overflow-y: auto;
`

const ErrorTitle = styled.div`
  font-weight: 600;
  color: #c62828;
  margin-bottom: 8px;
  font-size: 14px;
`

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
`

const ErrorItem = styled.li`
  color: #d32f2f;
  font-size: 13px;
  margin-bottom: 4px;
  line-height: 1.4;
`

export const BulkUserManagementModal = () => {
  const {
    state: { modal },
    setModal
  } = usePeoplePageContext()
  const {
    state: { employerId, employerInfo }
  } = useEmployerInfoContext()

  const [file, setFile] = useState<File | null>(null)
  const [diffData, setDiffData] = useState<BulkUserManagementResponse | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [fullReplaceMode, setFullReplaceMode] = useState(false)

  const { mutateAsync: bulkUserManagement, isLoading: isProcessing } = useBulkUserManagementQuery()
  const { mutateAsync: downloadTemplate, isLoading: isDownloading } =
    useDownloadUserManagementTemplate()

  const resetState = () => {
    setFile(null)
    setDiffData(null)
    setValidationErrors([])
    setFullReplaceMode(false)
  }

  const createFormData = (file: File): FormData => {
    const formData = new FormData()
    formData.append('file', file)
    return formData
  }

  const setShowModal = (show: boolean) => {
    if (!show) {
      resetState()
    }
    setModal(show ? ModalType.BulkUserManagement : undefined)
  }

  const handleDownloadTemplate = async () => {
    if (employerId) {
      await downloadTemplate({ employerId, employerName: employerInfo?.name })
    }
  }

  const handleUploadDryRun = async () => {
    if (!file || !employerId) return

    const formData = createFormData(file)

    try {
      const response = await bulkUserManagement({
        employerId,
        formData,
        dryRun: true,
        fullReplaceMode
      })

      if (response.success && response.diff && response.summary) {
        setDiffData(response)
        setValidationErrors([])
      } else {
        const errors = response.errors
          ? formatErrorsToArray(response.errors)
          : [response.message || 'Validation failed']

        setValidationErrors(errors)
        toast.error(`CSV validation failed - see errors below`)
        console.error('Validation failed:', response)
      }
    } catch (error) {
      console.error('Dry run error:', error)
      toast.error('An unexpected error occurred while processing the CSV')
    }
  }

  const handleConfirmChanges = async () => {
    if (!file || !employerId) return

    const formData = createFormData(file)

    try {
      const response = await bulkUserManagement({
        employerId,
        formData,
        dryRun: false,
        fullReplaceMode
      })

      if (response.success) {
        setShowModal(false)
      } else {
        const errorMessage = response.errors
          ? formatErrorMessage(response.errors)
          : response.message || 'Failed to apply changes'

        toast.error(`Failed to apply changes: ${errorMessage}`)
        console.error('Apply changes failed:', response)
      }
    } catch (error) {
      console.error('Confirm changes error:', error)
      toast.error('An unexpected error occurred while applying changes')
    }
  }

  const handleFileChange = (newFile: File | null) => {
    setFile(newFile)
    setDiffData(null)
    setValidationErrors([])
  }

  const handleBack = () => {
    resetState()
  }

  return (
    <Modal
      showModal={modal === ModalType.BulkUserManagement}
      setShowModal={setShowModal}
      minWidth={800}
      maxWidth={1400}
    >
      <ModalHeader>Bulk User Management</ModalHeader>
      <ModalBody>
        <Container>
          {validationErrors.length > 0 && (
            <ErrorContainer>
              <ErrorTitle>
                {validationErrors.length} Validation Error{validationErrors.length > 1 ? 's' : ''}
              </ErrorTitle>
              <ErrorList>
                {validationErrors.map((error, idx) => (
                  <ErrorItem key={idx}>{error}</ErrorItem>
                ))}
              </ErrorList>
            </ErrorContainer>
          )}

          {!diffData ? (
            <UploadView
              onDownloadTemplate={handleDownloadTemplate}
              onFileChange={handleFileChange}
              isDownloading={isDownloading}
            />
          ) : (
            <DiffPreviewView diff={diffData.diff} summary={diffData.summary} />
          )}
        </Container>
      </ModalBody>

      <ButtonContainer gap={spacingSM}>
        {!diffData ? (
          <>
            <FlexRow gap={spacingXS} style={{ alignItems: 'center' }}>
              <Checkbox
                labelText="Full Replace Mode"
                value={fullReplaceMode}
                onClick={() => setFullReplaceMode(!fullReplaceMode)}
              />
              <HoverIconCard
                iconSrc={AppIconMap[AppIcons.InfoCircle]}
                altText="Full Replace Mode info"
              >
                <span>
                  When enabled, users NOT in the CSV will be marked for removal. Use with caution.
                </span>
              </HoverIconCard>
            </FlexRow>
            <StyledButton secondary onClick={() => setShowModal(false)} id="cancel.label" />
            <StyledButton
              primary
              onClick={handleUploadDryRun}
              disabled={!file || isProcessing}
              isLoading={isProcessing}
              id="users.bulkUserManagement.preview"
            />
          </>
        ) : (
          <>
            <StyledButton secondary onClick={handleBack} id="users.bulkUserManagement.back" />
            <StyledButton secondary onClick={() => setShowModal(false)} id="cancel.label" />
            <StyledButton
              primary
              onClick={handleConfirmChanges}
              disabled={isProcessing}
              isLoading={isProcessing}
              id="users.bulkUserManagement.confirm"
            />
          </>
        )}
      </ButtonContainer>
    </Modal>
  )
}
